import { createContext, Fragment, useCallback, useContext, useMemo, useState } from 'react';

import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { ALL_MARKETS, isSearchLocation, setSearchedLocation } from '~/util/searchUtils';

import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { useVendorSearchNavigation } from '../../contexts/VendorSearchNavigationContext';
import { PartialTaxonomyNode } from '../../types/types';

type SRPSearchBarValues = {
  selectedLocation: LocationOrAllMarketsType;
  updateSelectedLocation: (location: LocationOrAllMarketsType) => void;
  nextTaxonomySearch: PartialTaxonomyNode | null;
  setNextTaxonomySearch: (t: PartialTaxonomyNode | null) => void;
  locationDropdownInput: string;
  setLocationDropdownInput: (t: string) => void;
  vendorDropdownInput: string;
  setVendorDropdownInput: (t: string) => void;
  clearSelections: () => void;
};

const SRPSearchBarContext = createContext<SRPSearchBarValues>({
  selectedLocation: ALL_MARKETS,
  updateSelectedLocation: () => undefined,
  nextTaxonomySearch: null,
  setNextTaxonomySearch: () => undefined,
  locationDropdownInput: '',
  setLocationDropdownInput: () => undefined,
  vendorDropdownInput: '',
  setVendorDropdownInput: () => undefined,
  clearSelections: () => undefined,
});

export const useSRPSearchBarContext = () => useContext(SRPSearchBarContext);

export const SRPSearchBarProvider: React.FC = ({ children }) => {
  const { vendorSearch } = useSearchResultsPageContext();

  const { tempSelectedLocation, setTempSelectedLocation } = useVendorSearchNavigation();

  const [nextTaxonomySearch, setNextTaxonomySearch] = useState<PartialTaxonomyNode | null>(
    vendorSearch?.selectedTaxonomy || null
  );

  const updateSelectedLocation = useCallback(
    (location: LocationOrAllMarketsType) => {
      setSearchedLocation(location);
      setTempSelectedLocation(location);
    },
    [setTempSelectedLocation]
  );

  const [locationDropdownInput, setLocationDropdownInput] = useState(
    vendorSearch.selectedLocation?.label || ''
  );
  const [vendorDropdownInput, setVendorDropdownInput] = useState('');

  const clearSelections = useCallback(() => {
    if (vendorSearch.selectedLocation) {
      updateSelectedLocation(vendorSearch.selectedLocation);
    }
    if (isSearchLocation(vendorSearch.selectedLocation)) {
      setLocationDropdownInput(vendorSearch.selectedLocation.label);
    } else {
      setLocationDropdownInput('');
    }
    setNextTaxonomySearch(vendorSearch.selectedTaxonomy || null);
    setVendorDropdownInput(vendorSearch.selectedTaxonomy?.label || '');
  }, [updateSelectedLocation, vendorSearch.selectedLocation, vendorSearch.selectedTaxonomy]);

  const selectedLocation = useMemo(
    () => tempSelectedLocation || ALL_MARKETS,
    [tempSelectedLocation]
  );

  return (
    <SRPSearchBarContext.Provider
      value={{
        updateSelectedLocation,
        selectedLocation,
        nextTaxonomySearch,
        setNextTaxonomySearch,
        locationDropdownInput,
        setLocationDropdownInput,
        vendorDropdownInput,
        setVendorDropdownInput,
        clearSelections,
      }}
    >
      <Fragment>{children}</Fragment>
    </SRPSearchBarContext.Provider>
  );
};
